import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Accordion, Alert, Icon } from '@seeqdev/qomponents';
import {
  provideVisualizationData,
  serializeDisplayRange,
} from '@/annotation/ckEditorPlugins/components/content.utilities';
import type { TableColumnFilter } from '@/core/tableUtilities/tables';
import type { Timezone } from '@/datetime/timezone.service';
import type { RangeExport } from '@/trendData/duration.store';
import { NewLineToBr } from '@/core/NewLineToBr.atom';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { HEADLESS_AG_RENDERING_CLASS, TableBuilderMode } from '@/tableBuilder/tableBuilder.constants';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import type { ParametersMap } from '@/utilities/formula.constants';
import { COLUMNS_AND_STATS } from '@/trendData/trendData.constants';
import { TableBuilderSimpleAgGrid } from '@/tableBuilder/TableBuilderSimpleAgGrid.organism';
import { TableBuilderConditionAgGrid } from '@/tableBuilder/TableBuilderConditionAgGrid.organism';
import type {
  AgGridChartMenuSettings,
  AgGridProps,
  ColumnOrRowWithDefinitions,
  ColumnToThresholdsCondition,
  ColumnToThresholdsSimple,
  ConditionTableColumnsAndRows,
  ConditionTableData,
  ContentMeasurements,
  OtherColumns,
  SimpleTableRow,
  TableBuilderHeaders,
  TableBuilderRowData,
  TableBuilderSortDirection,
  TableTextFormatter,
  TreemapAsset,
  TreemapViewSettings,
} from '@/tableBuilder/tableBuilder.types';
import { useDidMount } from 'rooks';
import type { ChartModel } from 'ag-grid-community';
import clsx from 'clsx';
import { TableSetupHelp } from '@/tableBuilder/tableComponents/TableSetupHelp';

export interface ChartSettingsInterface {
  title: string;
  legend: boolean;
  dataLabels: boolean;
  categoryLabels: boolean;
  chartType?: string;
  categoryColumns: string[];
  rows: string[];
  columns: string[];
  showSettings?: boolean;
  position?: { x: number; y: number };
  stacked?: boolean;
}

export interface TableBuilderPropsOnlyProps extends Omit<AgGridProps, 'otherColumns'> {
  canEdit: boolean;
  isSimpleMode: boolean;
  startMode: boolean;
  isLoading: boolean;
  hasOnlyStringSeries: boolean;
  hasNumericAndStringSeries: boolean;
  hasOnlyStringMetrics: boolean;
  hasNumericAndStringMetrics: boolean;
  fromDashboard?: boolean;
  setCellText: (key: string, value: string, id?: string) => void;
  setHeaderText: (columnKey: string, text: string) => void;
  setColumnFilter: (key: string, filter: TableColumnFilter) => void;
  simpleTableData: SimpleTableRow[];
  conditionTableData: ConditionTableData;
  headers: TableBuilderHeaders;
  simpleColumns: ColumnOrRowWithDefinitions[];
  conditionColumns: ConditionTableColumnsAndRows;
  isTransposed: boolean;
  useSignalColorsInChart: boolean;
  isTableStriped: boolean;
  isPresentationMode: boolean;
  isViewOnlyMode: boolean;
  displayRange: RangeExport;
  timezone: Timezone;
  displayMetricOnTrend: (metricId: string, formulaItemId: string, start: number, end: number, event: any) => void;
  columnToThresholdsForCondition: ColumnToThresholdsCondition;
  columnToThresholdsForSimple: ColumnToThresholdsSimple;
  distinctStringValueMap: {
    [mode: string]: {
      [columnKey: string]: string[];
    };
  };
  textFormatter: TableTextFormatter;
  moveColumn: (key: string, newKey: string) => void;
  canSort: boolean;
  maxSortLevel: number;
  sortByColumn: ((key: string, direction: TableBuilderSortDirection) => void) | undefined;
  removeColumn: (key: string) => void;
  setActiveTool: (toolName: string) => void;
  fetchFailedMessage:
    | {
        message: string;
        apiMessage: string;
      }
    | undefined;
  setActiveRefElement: (element: Node | null) => void;
  showChartView: boolean;
  showConditionChartView: boolean;
  showTreemapView: boolean;
  chartViewSettings: ChartSettingsInterface | ChartModel;
  treemapViewSettings: TreemapViewSettings;
  areAllRowsExpanded: boolean;
  chartViewConditionSettings: ChartModel;
  fetchStringColumnValues: (columnKey: string, isStringColumn: boolean, cancellationGroup: string) => void;
  setChartViewSettings: (property: any) => void;
  onContentLoad?: () => void;
  updateContentMeasurements?: (measurements: ContentMeasurements) => void;
  chartColors: ParametersMap;
  darkMode: boolean;
  otherColumns?: OtherColumns;
  hasMoreData?: boolean;
  setHideColumnHeadersWhenCopying: React.Dispatch<React.SetStateAction<boolean>>;
  tableItems: any[];
  rawConditionTableData: ConditionTableData;
  rawSimpleTableData: SimpleTableRow[];
  hideInteractiveContentActions?: boolean;
  contentResizeState?: number;
  isFormulaAcrossTableProcessing: boolean;
  isFormulaAcrossTable: boolean;
  afterChartUpdate?: () => void;
  statRowsIsSelected: (data: TableBuilderRowData[]) => boolean;
  chartMenuSettings: AgGridChartMenuSettings;
  handleSwapAssets: (asset: TreemapAsset) => void;
  proportionBySignal?: string;
  proportionByColumn?: string;
  isUniformSizing: boolean;
  treemapSquareScalingInverse: boolean;
  saveTreemapConfiguration: (
    selectedSignal: any,
    selectedColumn: any,
    treemapSquareScalingInverse: boolean,
    isUniformSizing: boolean,
  ) => void;
}

export const TableBuilderPropsOnly: React.FunctionComponent<TableBuilderPropsOnlyProps> = (
  props: TableBuilderPropsOnlyProps,
) => {
  const {
    isSimpleMode,
    startMode,
    canEdit,
    conditionTableData,
    isPresentationMode,
    isTransposed,
    isTableStriped,
    columnToThresholdsForCondition,
    columnToThresholdsForSimple,
    distinctStringValueMap,
    conditionColumns,
    simpleColumns,
    fetchFailedMessage,
    setActiveTool,
    showChartView: showSimpleChartView,
    showConditionChartView,
    onContentLoad,
    updateContentMeasurements,
    onAgGridReady,
    isFormulaAcrossTableProcessing,
    isFormulaAcrossTable,
    statRowsIsSelected,
    chartMenuSettings,
    proportionBySignal,
    proportionByColumn,
    isUniformSizing,
    treemapSquareScalingInverse,
    saveTreemapConfiguration,
  } = props;
  const { t } = useTranslation();
  const [activeAccordion, setActiveAccordion] = useState<string>('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isConditionSetupHelpDisplayed, setIsConditionSetupHelpDisplayed] = useState(false);

  useDidMount(() => {
    onContentLoad?.();
  });

  const hasFetchError = !_.isEmpty(fetchFailedMessage?.message);

  const statColumnIsSelected = () =>
    _.chain(simpleColumns)
      .some((col) => col.key.startsWith('statistic') || col.key === COLUMNS_AND_STATS.metricValue.key)
      .value();

  const showSetupHelp =
    ((!statColumnIsSelected() && showSimpleChartView) || _.isEmpty(simpleColumns)) && !startMode && isSimpleMode;

  // If there is an error ag-grid won't render, so fake that it finished to ensure the screenshot finishes (CRAB-41739)
  useEffect(() => {
    if (hasFetchError || startMode || showSetupHelp || isConditionSetupHelpDisplayed) {
      onAgGridReady?.();
      wrapperRef.current
        ?.querySelector(`.${HEADLESS_AG_RENDERING_CLASS}`)
        ?.classList.remove(HEADLESS_AG_RENDERING_CLASS);
    }
  }, [onAgGridReady, hasFetchError, startMode, showSetupHelp, wrapperRef.current]);

  if (headlessRenderMode() && !startMode) {
    provideVisualizationData({ ...props, visualization: Visualization.TABLE }, { displayRange: serializeDisplayRange });
  }

  // Ensures alert is not centered in headless mode, because otherwise renderer only captures empty space
  const alertWrapper = (alert: React.ReactNode) => (
    <div className={isPresentationMode ? 'width-300' : 'flexRowContainer flexCenter flexFill'}>{alert}</div>
  );

  return (
    <>
      <div
        id="tableBuilder"
        ref={wrapperRef}
        className={clsx('flexRowContainer flexFill', {
          p20: !(isPresentationMode || (isSimpleMode ? showSimpleChartView : showConditionChartView)),
        })}>
        {showSetupHelp && alertWrapper(<TableSetupHelp isTransposed={isTransposed} />)}
        {hasFetchError &&
          !startMode &&
          alertWrapper(
            <Alert
              dismissible={false}
              variant="theme"
              testId="has-fetch-error-alert"
              extraClassNames="max-width-400 screenshotSizeToContent fs fs16">
              <NewLineToBr lineToBreak={fetchFailedMessage?.message ?? ''} />

              {fetchFailedMessage?.apiMessage && (
                <Accordion
                  extraClassNames="pt10 font-size-smaller"
                  value={activeAccordion}
                  onItemSelect={(key) => setActiveAccordion(key)}
                  testId="apiMessage"
                  accordionItems={[
                    {
                      value: '0',
                      id: '0',
                      trigger: (
                        <div
                          data-testid="detailsToggle"
                          className="sq-text-primary cursorPointer flexColumnContainer flexAlignCenter flexSpaceBetween">
                          <span className="pr5">{t('TABLE_BUILDER.VIEW_DETAILS')}</span>
                          <Icon icon={activeAccordion === '0' ? 'fa-chevron-down' : 'fa-chevron-right'} />
                        </div>
                      ),
                      content: <span>{fetchFailedMessage.apiMessage}</span>,
                    },
                  ]}
                />
              )}
            </Alert>,
          )}
        {simpleColumns.length > 0 &&
          isSimpleMode &&
          !startMode &&
          !hasFetchError &&
          !showSetupHelp &&
          !(isFormulaAcrossTable && isFormulaAcrossTableProcessing) && (
            <TableBuilderSimpleAgGrid
              {...props}
              isStriped={isTableStriped}
              sortByColumn={props.sortByColumn ?? _.noop}
              columnToThresholds={columnToThresholdsForSimple}
              distinctStringValueMap={distinctStringValueMap?.[TableBuilderMode.Simple]}
              otherColumns={props.otherColumns?.simple ?? {}}
              updateContentMeasurements={updateContentMeasurements}
              chartMenuSettings={chartMenuSettings}
              proportionBySignal={proportionBySignal}
              proportionByColumn={proportionByColumn}
              isUniformSizing={isUniformSizing}
              treemapSquareScalingInverse={treemapSquareScalingInverse}
              saveTreemapConfiguration={saveTreemapConfiguration}
            />
          )}
        {!isSimpleMode && !startMode && !hasFetchError && (
          <TableBuilderConditionAgGrid
            {...props}
            tableData={conditionTableData}
            columns={conditionColumns}
            isStriped={isTableStriped}
            sortByColumn={props.sortByColumn ?? _.noop}
            columnToThresholds={columnToThresholdsForCondition}
            distinctStringValueMap={distinctStringValueMap?.[TableBuilderMode.Condition]}
            otherColumns={props.otherColumns?.condition ?? {}}
            setIsConditionSetupHelpDisplayed={setIsConditionSetupHelpDisplayed}
            statRowsIsSelected={statRowsIsSelected}
            alertWrapper={alertWrapper}
            chartMenuSettings={chartMenuSettings}
          />
        )}
        {startMode &&
          alertWrapper(
            <Alert dismissible={false} variant="theme" extraClassNames="width-300 fs16 p25 screenshotSizeToContent">
              {!canEdit && <p>{t('TABLE_BUILDER.START_MODE_NO_CONTENT')}</p>}
              {canEdit && (
                <>
                  <p>{t('TABLE_BUILDER.START_MODE')}</p>
                  <a className="cursorPointer" onClick={() => setActiveTool(TREND_TOOLS.THRESHOLD_METRIC)}>
                    <Icon icon="fa-plus-circle" extraClassNames="p5" type="theme" testId="addFirstMetricButton" />
                    <span>{t('TABLE_BUILDER.ADD_METRIC')}</span>
                  </a>
                </>
              )}
            </Alert>,
          )}
      </div>
    </>
  );
};
